import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import DomainMultiSelect from "@/components/DomainMultiSelect";
import Dropdown from "@/components/Dropdown";
import ErrorNotice from "@/components/ErrorNotice";
import FileDropzone from "@/components/FileDropzone";
import GithubPicker, { type GithubTarget } from "@/components/GithubPicker";
import { GithubMark } from "@/components/GithubBits";
import { createSkill, getGithubStatus, getSkill, importFromGithub, listTeams } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { MAX_DOMAINS } from "@/lib/domains";
import { describeGithubError, type DescribedError } from "@/lib/github-errors";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import type { SkillDetail, Team } from "@/lib/types";
import SkillPipelineProgress from "@/components/SkillPipelineProgress";
import { SkillRejectionReport } from "@/components/SkillRejectionReport";

type Phase = "form" | "running" | "rejected";
type Mode = "upload" | "github";
type Visibility = "public" | "private" | "team";

const MAX_TAGS = 4;

export default function NewSkillPage() {
  useDocumentTitle("SkillPatch");
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [visibility, setVisibility] = useState<Visibility>("private");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [domains, setDomains] = useState<string[]>([]);

  const [mode, setMode] = useState<Mode>("upload");
  const [ghConnected, setGhConnected] = useState<boolean | null>(null);
  const [ghTarget, setGhTarget] = useState<GithubTarget>({ repo: "", branch: "main", subpath: "" });

  const [phase, setPhase] = useState<Phase>("form");
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ghError, setGhError] = useState<DescribedError | null>(null);

  // Teams the user belongs to — drives the "Team" visibility option (and whether
  // picking it publishes straight away or first prompts to create a team).
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [teamSlug, setTeamSlug] = useState<string>("");
  const [showNoTeamModal, setShowNoTeamModal] = useState(false);

  // Check the GitHub connection once, the first time the Import tab is opened.
  useEffect(() => {
    if (mode !== "github" || ghConnected !== null || !accessToken) return;
    getGithubStatus(accessToken)
      .then((s) => setGhConnected(s.connected && s.token_valid !== false))
      .catch(() => setGhConnected(false));
  }, [mode, ghConnected, accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    listTeams(accessToken).then(setMyTeams).catch(() => setMyTeams([]));
  }, [accessToken]);

  function selectVisibility(v: Visibility) {
    if (v === "team" && myTeams.length === 0) {
      setShowNoTeamModal(true);
      return;
    }
    setVisibility(v);
    if (v === "team" && !teamSlug) setTeamSlug(myTeams[0].slug);
  }

  function addTag() {
    const t = tagInput.trim().replace(/,$/, "");
    if (!t) return;
    if (tags.length >= MAX_TAGS) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setTagInput("");
      return;
    }
    setTags([...tags, t]);
    setTagInput("");
  }

  // Toggle a domain, enforcing the max. Deselect always works; select only until the cap.
  function toggleDomain(slug: string) {
    setDomains((cur) =>
      cur.includes(slug)
        ? cur.filter((d) => d !== slug)
        : cur.length >= MAX_DOMAINS
          ? cur
          : [...cur, slug],
    );
  }

  async function pollUntilDone(slug: string) {
    // The Scan → Security review runs server-side and finishes regardless of this
    // page, so a transient poll failure (proxy hiccup, pod restart) must NOT abort
    // the flow and strand the skill — ride out a few in a row before giving up.
    let consecutiveErrors = 0;
    for (let i = 0; i < 90; i++) {
      try {
        const s = await getSkill(slug, accessToken);
        consecutiveErrors = 0;
        setSkill(s);
        if (s.status === "active") {
          // brief green flash, then straight to the freshly-published skill
          setTimeout(() => navigate(`/skill/${s.slug}`), 1400);
          return;
        }
        if (s.status === "rejected") {
          // Show the report here; the rejected skill is kept (never public — it failed
          // review) and surfaced in the dashboard's "Rejected" section for a week so the
          // uploader can revisit why, then the server reaper removes it.
          setPhase("rejected");
          return;
        }
      } catch {
        consecutiveErrors += 1;
        if (consecutiveErrors >= 6) {
          setError("Lost connection while reviewing — check your dashboard in a minute.");
          return;
        }
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    setError("The review is taking longer than usual — check your dashboard in a minute.");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setGhError(null);
    // "Team" isn't a real stored visibility — it's a private skill plus a team grant
    // seeded at publish time (see backend's team_slug handling).
    const apiVisibility = visibility === "team" ? "private" : visibility;
    const apiTeamSlug = visibility === "team" ? teamSlug : undefined;
    if (mode === "github") {
      if (!ghTarget.repo) {
        setGhError({ tone: "error", title: "Choose a repository", detail: "Pick a repository to import from." });
        return;
      }
      setPhase("running");
      try {
        const created = await importFromGithub(
          {
            repo: ghTarget.repo,
            branch: ghTarget.branch,
            subpath: ghTarget.subpath.trim(),
            visibility: apiVisibility,
            tags,
            domains,
            team_slug: apiTeamSlug,
          },
          accessToken!,
        );
        setSkill(created);
        await pollUntilDone(created.slug);
      } catch (err) {
        setPhase("form");
        setGhError(describeGithubError(err, "import"));
      }
      return;
    }
    if (!file) return;
    setPhase("running");
    try {
      const created = await createSkill(file, apiVisibility, accessToken!, tags, domains, apiTeamSlug);
      setSkill(created);
      await pollUntilDone(created.slug);
    } catch (err) {
      setPhase("form");
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  }

  function reset() {
    setPhase("form");
    setSkill(null);
    setError(null);
    setGhError(null);
  }

  // Wait for session restore before deciding logged-out, so a hard refresh of
  // /skills/new doesn't flash the login prompt before the token arrives.
  // Gating is handled by the <RequireAuth> route guard; kept as a defensive /
  // type-narrowing check (the page never mounts for a logged-out user).
  if (!user || !accessToken) return null;

  return (
    <main className="mx-auto max-w-4xl px-6 py-12">
      {/* Hero */}
      <header className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/[0.08] px-3.5 py-1.5">
          <ShieldIcon />
          <span className="font-mono text-[11px] tracking-wide text-primary">
            Every skill runs Scan → Security before going live
          </span>
        </span>
        <h1 className="mt-4 font-display text-3xl font-normal tracking-tight text-text">Publish skill</h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-text/55">
          Upload your skill <b className="text-text/80">folder</b> directly — or a{" "}
          <code className="text-primary">.tar</code>, <code className="text-primary">.tar.gz</code>, or{" "}
          <code className="text-primary">.zip</code> — with your{" "}
          <code className="text-primary">SKILL.md</code>. It runs the{" "}
          <b className="text-text/80">Scan → Security</b> pipeline before going live.
        </p>
      </header>

      {phase === "form" ? (
        <form onSubmit={onSubmit} className="mt-9 space-y-8">
          {/* 0 · Source toggle */}
          <div className="inline-flex w-full rounded-xl border border-white/10 bg-white/[0.03] p-1 font-display text-sm font-medium">
            {([
              { id: "upload" as const, label: "Upload archive", icon: <UploadIcon /> },
              { id: "github" as const, label: "Import from GitHub", icon: <GithubMark size={16} /> },
            ]).map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => { setMode(o.id); setError(null); setGhError(null); }}
                className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 transition ${
                  mode === o.id ? "bg-primary text-white" : "text-text/55 hover:text-text/80"
                }`}
              >
                {o.icon}
                {o.label}
              </button>
            ))}
          </div>

          {/* 1 · Source */}
          {mode === "upload" ? (
            <section>
              <FieldLabel title="Skill archive" required />
              <div className="mt-3">
                <FileDropzone
                  file={file}
                  onPick={setFile}
                  accept=".tar,.tar.gz,.tgz,.zip"
                  extensions={[".tar", ".tar.gz", ".tgz", ".zip"]}
                  maxBytes={10 * 1024 * 1024}
                  allowFolder
                  hint="A folder, or a .tar / .tar.gz / .tgz / .zip containing SKILL.md · max 10 MB"
                />
              </div>
            </section>
          ) : (
            <section>
              <FieldLabel title="GitHub repository" required />
              {ghConnected === null ? (
                <p className="mt-3 text-sm text-text/45">Checking your GitHub connection…</p>
              ) : ghConnected ? (
                <div className="mt-3">
                  <GithubPicker token={accessToken!} value={ghTarget} onChange={setGhTarget} />
                </div>
              ) : (
                <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-200">
                  Connect your GitHub account first in{" "}
                  <Link to="/settings" className="font-medium underline">Settings</Link>.
                </div>
              )}
            </section>
          )}

          {/* 2 · Visibility */}
          <section>
            <FieldLabel title="Visibility" />
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {([
                { v: "public", label: "Public", desc: "Anyone can find and install it.", icon: <GlobeIcon /> },
                { v: "private", label: "Private", desc: "Only you and people you share with.", icon: <LockIcon /> },
                { v: "team", label: "Team", desc: "Visible to your team's members only.", icon: <TeamIcon /> },
              ] as const).map((o) => {
                const on = visibility === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => selectVisibility(o.v)}
                    className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                      on ? "border-primary bg-primary/10" : "border-white/10 bg-white/[0.015] hover:border-white/25"
                    }`}
                  >
                    <span className={`mt-0.5 ${on ? "text-primary" : "text-text/40"}`}>{o.icon}</span>
                    <span>
                      <span className={`flex items-center gap-2 font-display font-medium ${on ? "text-primary" : "text-text"}`}>
                        {o.label}
                        {on && <CheckBadge />}
                      </span>
                      <span className="mt-0.5 block text-xs text-text/55">{o.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {visibility === "team" && myTeams.length > 1 && (
              <div className="mt-3">
                <label className="mb-1.5 block font-display text-xs font-medium text-text/60">Publish to which team?</label>
                <Dropdown
                  value={teamSlug}
                  onChange={setTeamSlug}
                  ariaLabel="Publish to which team"
                  className="w-full"
                  options={[...myTeams].sort((a, b) => a.name.localeCompare(b.name)).map((t) => ({ value: t.slug, label: t.name }))}
                />
                <p className="mt-1.5 text-xs text-text/45">Team members get read access.</p>
              </div>
            )}
            {visibility === "team" && myTeams.length === 1 && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.015] px-3 py-2.5">
                <span className="text-primary"><TeamIcon /></span>
                <span className="font-display text-sm text-text/70">
                  Publishing to <span className="font-medium text-text">{myTeams[0].name}</span> <span className="text-text/45">(read access)</span>
                </span>
              </div>
            )}
          </section>

          {/* 3 · Domains (required) */}
          <section>
            <FieldLabel title="Domains" required hint={`${domains.length}/${MAX_DOMAINS}`} />
            <div className="mt-3">
              <DomainMultiSelect selected={domains} onToggle={toggleDomain} max={MAX_DOMAINS} />
            </div>
          </section>

          {/* 4 · Tags */}
          <section>
            <FieldLabel title="Tags" optional hint={`${tags.length}/${MAX_TAGS}`} />
            <div
              className={`mt-3 flex flex-wrap items-center gap-2 rounded-xl border bg-white/[0.015] px-3 py-2.5 transition ${
                tags.length >= MAX_TAGS ? "border-white/10" : "border-white/15 focus-within:border-primary"
              }`}
            >
              {tags.map((t) => (
                <span key={t} className="flex items-center gap-1.5 rounded-md bg-primary/15 px-2 py-1 text-xs font-medium text-primary">
                  {t}
                  <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} className="text-primary/60 hover:text-primary">
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {tags.length < MAX_TAGS && (
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
                    else if (e.key === "Backspace" && !tagInput && tags.length) setTags(tags.slice(0, -1));
                  }}
                  onBlur={addTag}
                  placeholder={tags.length ? "Add another…" : "e.g. git, productivity (press Enter)"}
                  className="min-w-[8rem] flex-1 bg-transparent py-0.5 text-sm outline-none placeholder:text-text/35"
                />
              )}
            </div>
          </section>

          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>
          )}
          <ErrorNotice error={ghError} />

          <div className="flex gap-3">
            <Link
              to="/home"
              className="flex items-center justify-center rounded-xl border border-white/15 px-5 py-3 font-display text-sm font-medium text-text/70 transition hover:bg-white/5"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={domains.length === 0 || (mode === "upload" ? !file : !ghConnected || !ghTarget.repo)}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-display font-medium text-white shadow-lg shadow-primary/20 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
            >
              <ShieldIconWhite /> {mode === "github" ? "Import skill" : "Publish skill"}
            </button>
          </div>
        </form>
      ) : (
        // -- Pipeline progress ------------------------------------------------
        <div className="mt-9">
          <SkillPipelineProgress skill={skill} />

          {phase === "rejected" && skill?.rejection && (
            <RejectionPanel
              stage={skill.rejection.stage}
              report={skill.rejection.report}
              onRetry={reset}
            />
          )}

          {error && phase !== "rejected" && (
            <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700 dark:text-amber-200">
              {error}{" "}
              <Link to="/home" className="underline">Go to home</Link>
            </div>
          )}
        </div>
      )}

      {showNoTeamModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          onClick={() => setShowNoTeamModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#111] p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
              <TeamIcon />
            </div>
            <h3 className="mt-4 font-display text-base font-medium text-text">
              You're not part of a team yet
            </h3>
            <p className="mt-2 text-sm text-text/55">
              Create a team first, then you can publish skills directly to it.
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setShowNoTeamModal(false)}
                className="flex-1 rounded-xl border border-white/15 py-2.5 font-display text-sm font-medium text-text/70 transition hover:bg-white/5"
              >
                Cancel
              </button>
              <Link
                to="/home?section=teams"
                onClick={() => setShowNoTeamModal(false)}
                className="flex-1 rounded-xl bg-primary py-2.5 font-display text-sm font-medium text-white transition hover:opacity-95"
              >
                Create a team
              </Link>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
function RejectionPanel({
  stage,
  report,
  onRetry,
}: {
  stage: string;
  report: Record<string, unknown>;
  onRetry: () => void;
}) {
  return (
    <SkillRejectionReport
      stage={stage}
      report={report}
      className="mt-6"
      footer={
        <>
          <button onClick={onRetry} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-95">
            Upload a fix
          </button>
          <Link to="/home" className="rounded-lg border border-white/15 px-4 py-2 text-sm text-text/80 hover:bg-white/5">
            Back to home
          </Link>
        </>
      }
    />
  );
}

// -- Bits -------------------------------------------------------------------
function FieldLabel({ title, optional, required, hint }: { title: string; optional?: boolean; required?: boolean; hint?: string }) {
  const rightText = optional && hint ? `optional · ${hint}` : optional ? "optional" : hint;
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-text/45">
        <span className="h-px w-5 bg-primary" />
        {title}
        {required && <span className="ml-0.5 normal-case text-red-400" title="Required">*</span>}
      </span>
      {rightText && <span className="ml-auto font-mono text-[10px] text-text/35">{rightText}</span>}
    </div>
  );
}

// -- Icons (inline, currentColor) -------------------------------------------
const ic = "h-5 w-5";
function UploadIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V4M7 9l5-5 5 5" /><path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" /></svg>); }
function GlobeIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" /></svg>); }
function LockIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>); }
function TeamIcon() { return (<svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>); }
function ShieldIcon() { return (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>); }
function ShieldIconWhite() { return (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>); }
function CheckBadge() { return (<svg className="h-3.5 w-3.5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>); }
function XIcon({ className = ic }: { className?: string }) { return (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>); }
